import {
    htmlToSlate,
    RichText,
    slateToHtml,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@amelio/ui-library';
import DOMPurify from 'dompurify';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CompetencySelectionModal } from '../../PrepareMeeting/components/CompetencySelectionModal/CompetencySelectionModal';
import { useKeyCompetencies } from '../../PrepareMeeting/hooks/useKeyCompetencies';
import { PenIcon, PlusIcon, SparklesIcon, XIcon } from '../PrepareMeetingIcons';
import { HandoutSectionTitle } from './HandoutSectionTitle';

import type { ProficiencyLevel } from '@/types/competencies/ProficiencyLevel';
import type { KeyCompetencyItem, KeyCompetencyListType } from '@/types/review/KeyCompetencies';
import type { CompetencyGroup } from '@/types/review/ReviewDiscussion';

import { ProficiencyIcon } from '@/components/ProficiencyIcon/ProficiencyIcon';
import { resolveRankedProficiencyLevelColor } from '@/components/ProficiencyIcon/resolveProficiencyLevelColor';
import { getIconEntry } from '@/constants/proficiencyIconCatalog';
import { showWarningToast } from '@/plugins/Toast';
import { normalizeFrenchLanguageCode } from '@/utils/languageCode';
import { getLocalizedText } from '@/utils/localization';
import { orderProficiencyLevels } from '@/utils/proficiencyLevel';

interface HandoutCompetenciesProps {
    /** The cycle whose Mastered/Growth lists these are. */
    cycleInstanceId: string;
    competencyGroups: CompetencyGroup[];
    proficiencyLevels: ProficiencyLevel[];
}

interface CompetencyEntry {
    id: string;
    name: string;
    description: string;
    assessedScore: number;
    groupName: string;
    proficiencyLevelId?: string;
    lastAssessedAt: string | null;
}

const mapCompetencies = (groups: CompetencyGroup[]) => {
    return groups.flatMap((group) =>
        group.competencies.map((competency) => ({
            id: competency.id,
            name: competency.name,
            description: competency.description ?? '',
            assessedScore: competency.assessedScore,
            groupName: group.name,
            proficiencyLevelId: competency.proficiencyLevelId,
            lastAssessedAt: competency.lastAssessedAt ?? null,
        })),
    );
};

const htmlHasText = (html?: string): boolean =>
    Boolean(
        html
            ?.replace(/<[^>]*>/g, '')
            .replace(/&nbsp;/gi, ' ')
            .trim(),
    );

/** The two lists shown in Prepare Meeting, keyed to the backend list type. */
const SECTIONS: { type: KeyCompetencyListType; icon: string; cardClass: string; accent: string }[] =
    [
        { type: 'Mastered', icon: '✓', cardClass: 'bg-teal-50/70', accent: 'mastered' },
        { type: 'Growth', icon: '!', cardClass: 'bg-purple-50/70', accent: 'growth' },
    ];

export const HandoutCompetencies = ({
    cycleInstanceId,
    competencyGroups,
    proficiencyLevels,
}: HandoutCompetenciesProps) => {
    const { t, i18n } = useTranslation();
    // Manual entries are stored under the reader's content language ('en' | 'fr'); the other stays empty.
    const editLanguage = normalizeFrenchLanguageCode(i18n.language).toLowerCase().startsWith('fr')
        ? 'fr'
        : 'en';
    const orderedLevelIds = orderProficiencyLevels(proficiencyLevels).map((level) => level.id);

    const availableCompetencies = useMemo(
        () => mapCompetencies(competencyGroups),
        [competencyGroups],
    );

    const {
        mastered,
        growth,
        generatingList,
        reserveExhausted,
        error,
        regenerate,
        addItem,
        updateItem,
        removeItem,
    } = useKeyCompetencies(cycleInstanceId);

    const [openMenu, setOpenMenu] = useState<KeyCompetencyListType | null>(null);
    const [editing, setEditing] = useState<{ type: KeyCompetencyListType; index: number } | null>(
        null,
    );
    const [editValues, setEditValues] = useState({ name: '', description: '' });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalSectionType, setModalSectionType] = useState<KeyCompetencyListType>('Mastered');

    const itemsFor = (type: KeyCompetencyListType) => (type === 'Mastered' ? mastered : growth);

    const masteredModalCompetencies = useMemo(
        () =>
            mastered
                .map((item) => {
                    if (!item.competencyId) {
                        return null;
                    }
                    return {
                        id: item.competencyId,
                        name: getLocalizedText(item.name),
                        description: getLocalizedText(item.description),
                        assessedScore: 0,
                        groupName: '',
                        lastAssessedAt: null as string | null,
                    } satisfies CompetencyEntry;
                })
                .filter((x): x is CompetencyEntry => Boolean(x)),
        [mastered],
    );

    const growingModalCompetencies = useMemo(
        () =>
            growth
                .map((item) => {
                    if (!item.competencyId) {
                        return null;
                    }
                    return {
                        id: item.competencyId,
                        name: getLocalizedText(item.name),
                        description: getLocalizedText(item.description),
                        assessedScore: 0,
                        groupName: '',
                        lastAssessedAt: null as string | null,
                    } satisfies CompetencyEntry;
                })
                .filter((x): x is CompetencyEntry => Boolean(x)),
        [growth],
    );

    const startEdit = (type: KeyCompetencyListType, index: number, item: KeyCompetencyItem) => {
        setEditing({ type, index });
        setEditValues({
            name: getLocalizedText(item.name),
            description: getLocalizedText(item.description),
        });
    };

    const saveEdit = () => {
        if (!editing) return;
        const item = itemsFor(editing.type)[editing.index];
        const description = htmlHasText(editValues.description)
            ? editValues.description.trim()
            : '';
        // Update the reader's language, keep the other (like the agenda edit-merge).
        updateItem(editing.type, editing.index, {
            name: { ...item.name, [editLanguage]: editValues.name.trim() },
            description: { ...item.description, [editLanguage]: description },
        });
        setEditing(null);
    };

    const cancelEdit = () => {
        if (!editing) return;
        const item = itemsFor(editing.type)[editing.index];
        setEditValues({
            name: getLocalizedText(item.name),
            description: getLocalizedText(item.description),
        });
        setEditing(null);
    };

    const runRegenerate = (type: KeyCompetencyListType, mode: 'Replace' | 'Add') => {
        setOpenMenu(null);
        void regenerate(type, mode);
    };

    const handleOpenModal = (type: KeyCompetencyListType) => {
        setModalSectionType(type);
        setIsModalOpen(true);
    };

    const handleInsertFromModal = (competency: CompetencyEntry) => {
        addItem(modalSectionType, {
            competencyId: competency.id,
            name: { [editLanguage]: competency.name },
            description: { [editLanguage]: competency.description },
            source: 'selection',
        });
        setIsModalOpen(false);
    };

    const renderSection = ({ type, icon, cardClass, accent }: (typeof SECTIONS)[number]) => {
        const items = itemsFor(type);
        const isMastered = type === 'Mastered';
        const isGenerating = generatingList[type];
        const title = t(
            isMastered
                ? 'postReviewPrepareMeeting.competencies.mastered.title'
                : 'postReviewPrepareMeeting.competencies.growth.title',
        );

        return (
            <div
                key={type}
                className={'group/section pt-[40px]'}
                data-testid={`key-competencies-${accent}`}
                data-pdf-block={'true'}
            >
                <div
                    className={
                        'mb-[8px] flex flex-wrap items-center justify-between sm:flex-nowrap'
                    }
                >
                    <HandoutSectionTitle className={'sm:whitespace-nowrap'}>
                        {title}
                    </HandoutSectionTitle>

                    <div
                        className={'relative'}
                        data-pdf-hide={'true'}
                    >
                        <button
                            className={`text-font-xs flex items-center gap-2 rounded-[8px] border px-[10px] py-[4px] font-semibold transition-opacity disabled:cursor-not-allowed disabled:opacity-60 ${
                                isMastered
                                    ? 'hover:bg-primary/10 border-teal-200 text-teal-400'
                                    : 'border-secondary/40 text-secondary hover:bg-secondary/10'
                            }`}
                            type={'button'}
                            onClick={() =>
                                setOpenMenu((current) => (current === type ? null : type))
                            }
                            disabled={isGenerating}
                            data-testid={`regenerate-${accent}-button`}
                        >
                            <SparklesIcon
                                className={`h-[14px] w-[14px] ${
                                    isMastered ? 'text-teal-400' : 'text-secondary'
                                }`}
                            />
                            {isGenerating
                                ? t('postReviewPrepareMeeting.competencies.generating')
                                : t('postReviewPrepareMeeting.competencies.regenerateWithAI')}
                        </button>

                        {openMenu === type && !isGenerating && (
                            <>
                                <div
                                    className={'fixed inset-0 z-10'}
                                    onClick={() => setOpenMenu(null)}
                                />
                                <div
                                    className={
                                        'absolute right-0 z-20 mt-2 w-80 rounded-xl border border-gray-100 bg-white p-2 shadow-lg'
                                    }
                                    data-testid={`regenerate-${accent}-menu`}
                                >
                                    <TooltipProvider>
                                        <Tooltip delayDuration={200}>
                                            <TooltipTrigger asChild>
                                                <div className={'inline-block w-full'}>
                                                    <button
                                                        type={'button'}
                                                        className={`w-full rounded-lg px-4 py-3 text-left hover:bg-gray-50 ${
                                                            reserveExhausted[type]
                                                                ? 'cursor-not-allowed opacity-50'
                                                                : ''
                                                        }`}
                                                        onClick={() => {
                                                            if (reserveExhausted[type]) {
                                                                showWarningToast({
                                                                    message: t(
                                                                        'postReviewPrepareMeeting.competencies.replaceListedExhausted',
                                                                    ),
                                                                });
                                                                return;
                                                            }
                                                            runRegenerate(type, 'Replace');
                                                        }}
                                                        aria-disabled={reserveExhausted[type]}
                                                        data-testid={`regenerate-${accent}-replace`}
                                                    >
                                                        <p
                                                            className={
                                                                'text-sm font-semibold text-gray-900'
                                                            }
                                                        >
                                                            {t(
                                                                'postReviewPrepareMeeting.competencies.replaceListed',
                                                            )}
                                                        </p>
                                                        <p className={'text-xs text-gray-500'}>
                                                            {t(
                                                                'postReviewPrepareMeeting.competencies.replaceListedHint',
                                                            )}
                                                        </p>
                                                    </button>
                                                </div>
                                            </TooltipTrigger>
                                            {reserveExhausted[type] && (
                                                <TooltipContent side={'bottom'}>
                                                    {t(
                                                        'postReviewPrepareMeeting.competencies.replaceListedExhausted',
                                                    )}
                                                </TooltipContent>
                                            )}
                                        </Tooltip>
                                    </TooltipProvider>
                                    <TooltipProvider>
                                        <Tooltip delayDuration={200}>
                                            <TooltipTrigger asChild>
                                                <div className={'inline-block w-full'}>
                                                    <button
                                                        type={'button'}
                                                        className={
                                                            'w-full rounded-lg px-4 py-3 text-left hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
                                                        }
                                                        onClick={() => runRegenerate(type, 'Add')}
                                                        disabled={reserveExhausted[type]}
                                                        data-testid={`regenerate-${accent}-add`}
                                                    >
                                                        <p
                                                            className={
                                                                'text-sm font-semibold text-gray-900'
                                                            }
                                                        >
                                                            {t(
                                                                'postReviewPrepareMeeting.competencies.addThree',
                                                            )}
                                                        </p>
                                                        <p className={'text-xs text-gray-500'}>
                                                            {t(
                                                                'postReviewPrepareMeeting.competencies.addThreeHint',
                                                            )}
                                                        </p>
                                                    </button>
                                                </div>
                                            </TooltipTrigger>
                                            {reserveExhausted[type] && (
                                                <TooltipContent side={'bottom'}>
                                                    {t(
                                                        'postReviewPrepareMeeting.competencies.addThreeExhausted',
                                                    )}
                                                </TooltipContent>
                                            )}
                                        </Tooltip>
                                    </TooltipProvider>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <div className={'space-y-[8px]'}>
                    {items.map((item, index) => {
                        const isEditing = editing?.type === type && editing.index === index;
                        const backing = availableCompetencies.find(
                            (competency) => competency.id === item.competencyId,
                        );
                        const itemLevel = proficiencyLevels.find(
                            (level) => level.id === backing?.proficiencyLevelId,
                        );
                        return (
                            <div
                                key={item.competencyId ?? `manual-${index}`}
                                className={`group/card rounded-[8px] pt-[12px] pr-[12px] pb-[12px] pl-[16px] ${cardClass}`}
                                data-pdf-block={'true'}
                            >
                                <div className={'flex items-start justify-between gap-3'}>
                                    <div className={'flex min-w-0 flex-1 items-start gap-4'}>
                                        {!isEditing &&
                                            (itemLevel ? (
                                                <ProficiencyIcon
                                                    iconEntry={getIconEntry(itemLevel.icon)}
                                                    color={resolveRankedProficiencyLevelColor({
                                                        level: itemLevel,
                                                        orderedLevelIds,
                                                    })}
                                                    iconUrl={itemLevel.iconUrl}
                                                    className={
                                                        'mt-[6px] h-[20px] w-[20px] flex-shrink-0'
                                                    }
                                                />
                                            ) : (
                                                <span
                                                    className={`text-font-sm mt-[6px] flex items-center justify-center ${
                                                        isMastered
                                                            ? 'text-teal-400'
                                                            : 'text-secondary'
                                                    }`}
                                                >
                                                    {icon}
                                                </span>
                                            ))}
                                        <div className={'min-w-0 flex-1'}>
                                            {isEditing ? (
                                                <input
                                                    className={`text-font-sm w-full rounded-lg bg-white px-3 py-2 focus:outline-none ${
                                                        type === 'Mastered'
                                                            ? 'border border-teal-200 focus:border-teal-200 focus:ring-2 focus:ring-teal-200'
                                                            : 'border border-purple-200 focus:border-purple-200 focus:ring-2 focus:ring-purple-200'
                                                    }`}
                                                    value={editValues.name}
                                                    onChange={(event) =>
                                                        setEditValues((current) => ({
                                                            ...current,
                                                            name: event.target.value,
                                                        }))
                                                    }
                                                />
                                            ) : (
                                                <p className={'font-semibold text-gray-800'}>
                                                    {getLocalizedText(item.name)}
                                                </p>
                                            )}
                                            {isEditing ? (
                                                <RichText
                                                    value={htmlToSlate(editValues.description)}
                                                    onChange={(value) =>
                                                        setEditValues((current) => ({
                                                            ...current,
                                                            description: slateToHtml(value),
                                                        }))
                                                    }
                                                    placeholder={t(
                                                        'postReviewPrepareMeeting.competencies.skillDescriptionPlaceholder',
                                                    )}
                                                    locale={i18n.language}
                                                    toolbarOptions={{
                                                        headingOne: true,
                                                        headingTwo: true,
                                                        bold: true,
                                                        italic: true,
                                                        underline: true,
                                                        bulletList: true,
                                                        numberedList: true,
                                                        strikethrough: false,
                                                        link: false,
                                                    }}
                                                    className={`scrollbar-glass mt-2 h-[120px] overflow-y-scroll bg-white outline-none md:min-w-0 ${
                                                        type === 'Mastered'
                                                            ? '!border !border-teal-200 focus-within:!border-teal-200 focus-within:ring-2 focus-within:ring-teal-300 hover:!border-teal-200'
                                                            : '!border !border-purple-200 focus-within:!border-purple-200 focus-within:ring-2 focus-within:ring-purple-200 hover:!border-purple-200'
                                                    }`}
                                                />
                                            ) : htmlHasText(getLocalizedText(item.description)) ? (
                                                <div
                                                    className={
                                                        'text-md text-gray-500 [&_a]:text-blue-500 [&_a]:underline [&_em]:italic [&_h1]:mt-4 [&_h1]:mb-2 [&_h1]:text-[20px] [&_h1]:leading-tight [&_h1]:font-semibold [&_h2]:mt-3 [&_h2]:mb-1.5 [&_h2]:text-[16px] [&_h2]:leading-snug [&_h2]:font-semibold [&_li]:ml-4 [&_ol]:my-2 [&_ol]:list-inside [&_ol]:list-decimal [&_ol]:space-y-1 [&_p]:mb-2 [&_p]:last:mb-0 [&_s]:line-through [&_strong]:font-semibold [&_u]:underline [&_ul]:my-2 [&_ul]:list-inside [&_ul]:list-disc [&_ul]:space-y-1'
                                                    }
                                                    dangerouslySetInnerHTML={{
                                                        __html: DOMPurify.sanitize(
                                                            getLocalizedText(item.description),
                                                        ),
                                                    }}
                                                />
                                            ) : (
                                                <p className={'text-font-sm text-gray-500'}>
                                                    {t(
                                                        'postReviewPrepareMeeting.competencies.defaultDescription',
                                                    )}
                                                </p>
                                            )}

                                            {isEditing && (
                                                <div
                                                    className={'mt-3 flex justify-end gap-2'}
                                                    data-pdf-hide={'true'}
                                                >
                                                    <button
                                                        className={
                                                            'rounded-md border border-gray-200 px-4 py-2 text-sm'
                                                        }
                                                        type={'button'}
                                                        onClick={cancelEdit}
                                                    >
                                                        {t(
                                                            'postReviewPrepareMeeting.competencies.cancel',
                                                        )}
                                                    </button>
                                                    <button
                                                        className={
                                                            'bg-primary rounded-lg px-4 py-2 text-sm font-medium text-white hover:brightness-110'
                                                        }
                                                        type={'button'}
                                                        onClick={saveEdit}
                                                    >
                                                        {t(
                                                            'postReviewPrepareMeeting.competencies.save',
                                                        )}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {!isEditing && (
                                        <div
                                            className={'flex gap-2'}
                                            data-pdf-hide={'true'}
                                        >
                                            <button
                                                className={`rounded-[8px] ${
                                                    isMastered ? 'bg-teal-400' : 'bg-secondary'
                                                } p-[6px] opacity-0 transition-opacity group-hover/card:opacity-100 hover:brightness-90`}
                                                type={'button'}
                                                onClick={() => startEdit(type, index, item)}
                                            >
                                                <PenIcon
                                                    className={'h-[14px] w-[14px] text-white'}
                                                />
                                            </button>
                                            <button
                                                className={
                                                    'bg-error rounded-[8px] p-[6px] text-white opacity-0 transition-opacity group-hover/card:opacity-100 hover:brightness-90'
                                                }
                                                type={'button'}
                                                onClick={() => removeItem(type, index)}
                                            >
                                                <XIcon className={'h-[14px] w-[14px] text-white'} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div
                    className={'mt-[12px]'}
                    data-pdf-hide={'true'}
                >
                    <button
                        className={`focus-visible:ring-primary-800 flex items-center gap-2 rounded-md border border-dashed px-3 py-2 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                            isMastered
                                ? 'hover:bg-primary/10 border-teal-400 text-teal-400'
                                : 'border-secondary text-secondary hover:!bg-secondary/10'
                        }`}
                        type={'button'}
                        onClick={() => handleOpenModal(type)}
                        data-testid={`add-skill-${accent}-button`}
                    >
                        <PlusIcon
                            className={`h-[16px] w-[16px] ${isMastered ? 'text-teal-400' : 'text-secondary'}`}
                        />
                        {t('postReviewPrepareMeeting.competencies.addSkill')}
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div
            className={'mb-8'}
            data-testid={'handout-competency-lists'}
        >
            {error && (
                <p
                    className={'mb-3 text-sm text-red-600'}
                    data-pdf-hide={'true'}
                    data-testid={'key-competencies-error'}
                >
                    {error}
                </p>
            )}
            {SECTIONS.map(renderSection)}
            <CompetencySelectionModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onInsert={handleInsertFromModal}
                masteredCompetencies={masteredModalCompetencies}
                growingCompetencies={growingModalCompetencies}
                availableCompetencies={availableCompetencies}
                proficiencyLevels={proficiencyLevels}
                modalSectionType={modalSectionType}
            />
        </div>
    );
};
